import * as React from "react";

import { Box } from "@material-ui/core";
import * as Sentry from "@sentry/browser";
import * as Redux from "react-redux";

import { Link } from "client/components";
import {
  BankLegalAgreement,
  NZTermsAndConditions,
} from "client/domain/FormBuilder/BankInformation";
import { getSetBankFormComplete } from "client/domain/FormBuilder/BankInformation";
import {
  UIFieldMap,
  useMerchantModel,
  useSaveFormState,
  useSubmitBankInfo,
  useUiFieldMap,
} from "client/hooks";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { MerchantFieldMapper } from "client/lib/apt-merchant";
import { BankFieldSetNames } from "client/lib/apt-merchant/src/fieldsets";
import { addToGTMDataLayer } from "client/lib/gtm";
import { errorActions, formCompletionActions } from "client/store";

import { FormBuilder } from "../FormBuilder";

import { DirectDebitHelperText } from "./DirectDebitHelperText";

export type BankAccountFormFieldUiMap = MerchantFieldMapper<UIFieldMap>;

// field order reflects input order
const uiFieldMap: BankAccountFormFieldUiMap = {
  secondaryBank: {
    signatoryType2: { width: 12 },
    bankName2: {},
    bankBranch2: {},
    bankAccountName2: { width: 12 },
    routing2: { width: 12 },
    bsb2: {
      width: 12,
      inputMask: [/[0-9]/, /[0-9]/, /[0-9]/, "-", /[0-9]/, /[0-9]/, /[0-9]/],
    },
    sortCode2: {
      inputMask: [
        /[0-9]/,
        /[0-9]/,
        "-",
        /[0-9]/,
        /[0-9]/,
        "-",
        /[0-9]/,
        /[0-9]/,
      ],
    },
    account2: { width: 12 },
    confirmAccount2: { width: 12 },
    signatoryName2: {},
    email2: {},
    directDebitAgreement2: {
      customHelperText: <DirectDebitHelperText />,
      width: 12,
    },
  },
  primaryBank: {
    signatoryType: { width: 12 },
    bankName: {},
    bankBranch: {},
    bankAccountName: { width: 12 },
    routing: { width: 12 },
    canadaRoutingTransitIdNumber: { width: 6 },
    canadaRoutingInstitutionIdNumber: { width: 6 },
    bsb: {
      width: 12,
      inputMask: [/[0-9]/, /[0-9]/, /[0-9]/, "-", /[0-9]/, /[0-9]/, /[0-9]/],
    },
    sortCode: {
      inputMask: [
        /[0-9]/,
        /[0-9]/,
        "-",
        /[0-9]/,
        /[0-9]/,
        "-",
        /[0-9]/,
        /[0-9]/,
      ],
    },
    account: { width: 12 },
    confirmAccount: { width: 12 },
    canadaChequingAccount: { width: 12 },
    confirmCanadaChequingAccount: { width: 12 },
    signatoryName: {},
    email: {},
    directDebitAgreement: {
      customHelperText: <DirectDebitHelperText />,
      width: 12,
    },
  },
};

export const BankAccountForm: React.FC<{
  fieldSetName: BankFieldSetNames;
  heading: React.ReactNode;
  onSubmitSuccess?: () => void;
  overrideSubmitHandler?: () => void;
  includeSkipButton?: boolean;
}> = ({
  heading,
  includeSkipButton,
  fieldSetName,
  onSubmitSuccess,
  overrideSubmitHandler,
}) => {
  const [nzTermsAgreementChecked, setNZTermsAgreementChecked] = React.useState(
    false
  );
  const { orderedUiFieldMap } = useUiFieldMap(uiFieldMap);

  const [disableSubmit, setDisableSubmit] = React.useState(false);
  const { merchant, formatMerchantForBankInfo } = useMerchantModel();
  const dispatch = Redux.useDispatch();
  const fieldSet =
    fieldSetName === "PrimaryBankFieldSet"
      ? merchant.primaryBank
      : merchant.secondaryBank;

  const { submitBankInfo } = useSubmitBankInfo({
    onSuccess: ({ validation_errors, formCompletedState }) => {
      if (validation_errors) {
        addToGTMDataLayer({
          event: "PayInfoUpload",
          formSubmission: "Unsuccessful",
          country: merchant.companyInformation.registeredCountry.value,
        });
        dispatch(errorActions.setFieldErrorMap(validation_errors));
      } else if (formCompletedState) {
        if (onSubmitSuccess) {
          onSubmitSuccess();
        }
        dispatch(formCompletionActions.setFormCompletion(formCompletedState));
        logAmplitudeEvent(
          "REDESIGN_LINK_YOUR_BANK_ACCOUNT",
          "submitted_bank_info"
        );
        addToGTMDataLayer({
          event: "PayInfoUpload",
          formSubmission: "Successful",
          country: merchant.companyInformation.registeredCountry.value,
        });
      } else {
        dispatch(errorActions.setError("Unexpected error"));
        Sentry.captureException(
          new Error(
            `Unexpected error submitting bank info, merchantId: ${
              merchant.merchantAccount?.merchantId ?? "N/A"
            }, mlsProspectRef: ${
              merchant.merchantAccount?.mlsProspectRef ?? "N/A"
            }`
          )
        );
      }
    },
  });

  React.useEffect(() => {
    const isSubmitDisabled = () => {
      if (
        merchant.companyInformation.registeredCountry.value === "New Zealand"
      ) {
        // always allow account with "Multiple Signatories" to submit form
        if (
          fieldSetName == "PrimaryBankFieldSet" &&
          merchant.primaryBank.signatoryType.value === "One Signatory"
        ) {
          return !nzTermsAgreementChecked;
        }

        if (
          fieldSetName == "SecondaryBankFieldSet" &&
          merchant.secondaryBank.signatoryType2.value === "One Signatory"
        ) {
          return !nzTermsAgreementChecked;
        }
      }
      return false;
    };
    setDisableSubmit(isSubmitDisabled());
  }, [
    merchant.companyInformation.registeredCountry.value,
    merchant.primaryBank.signatoryType.value,
    merchant.secondaryBank.signatoryType2.value,
    nzTermsAgreementChecked,
  ]);

  const shouldSkipOnSubmit = () => {
    if (fieldSetName === "PrimaryBankFieldSet") {
      return false;
    }
    // if all fields are blank, treat the same as skipping the section and do not submit the form
    return (
      fieldSetName === "SecondaryBankFieldSet" &&
      fieldSet.fields
        .filter((field) => field.shown)
        .every((field) => !field.value)
    );
  };

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement> | React.MouseEvent
  ) => {
    event?.preventDefault();
    if (shouldSkipOnSubmit() && overrideSubmitHandler) {
      overrideSubmitHandler();
      return;
    }

    if (overrideSubmitHandler) {
      // with AU in-store card, our bank completion is a 3 step process, we need to update each step
      overrideSubmitHandler();
      return;
    }

    // If the user meets the conditions for the nz banking agreement
    // check to make sure that they have checked before submitting form
    if (disableSubmit) {
      dispatch(
        errorActions.setError(
          "Please accept the terms agreement before submitting."
        )
      );
      return;
    }

    const salesChannel = merchant.lead.salesChannel.value;
    const registeredCountry = merchant.lead.registeredCountry.value;

    const setBankFormComplete = getSetBankFormComplete({
      fieldSetName,
      salesChannel,
      registeredCountry,
    });

    const bankData = formatMerchantForBankInfo({
      fieldSetName,
      verificationSource: "MANUAL",
      setBankFormComplete,
    });

    await submitBankInfo(bankData);
  };

  const { saveFormState } = useSaveFormState();

  const handleSkip = () => {
    logAmplitudeEvent(
      "REDESIGN_LINK_YOUR_BANK_ACCOUNT",
      "clicked_skip_secondary_bank_form"
    );
    // If skipping the second form, make API call to /api/update-form-completion to
    // update bankForm:true directly in order to route to customer support form
    saveFormState({ bankForm: true });
  };

  return (
    <>
      <Box maxWidth={468}>
        {heading}
        <FormBuilder
          fieldSet={fieldSet}
          footer={
            <Box mt={3}>
              <BankLegalAgreement
                country={merchant.companyInformation.registeredCountry.value}
                agreementType="manual"
              />
              <NZTermsAndConditions
                checked={nzTermsAgreementChecked}
                setChecked={(_, checked) => {
                  setNZTermsAgreementChecked(checked);
                  if (checked) {
                    logAmplitudeEvent(
                      "REDESIGN_LINK_YOUR_BANK_ACCOUNT",
                      "checked_nz_direct_debit_terms_checkbo"
                    );
                  }
                }}
                signatoryType={
                  fieldSetName === "PrimaryBankFieldSet"
                    ? merchant.primaryBank.signatoryType.value
                    : merchant.secondaryBank.signatoryType2.value
                }
                registeredCountry={
                  merchant.companyInformation.registeredCountry.value
                }
              />
            </Box>
          }
          onSubmit={handleSubmit}
          uiFieldMap={
            fieldSetName === "PrimaryBankFieldSet"
              ? orderedUiFieldMap.primaryBank
              : orderedUiFieldMap.secondaryBank
          }
        />
      </Box>
      {includeSkipButton && (
        <Box mt={4}>
          <Link onClick={handleSkip} linkText="Skip this step" />
        </Box>
      )}
    </>
  );
};
